import {memo, useCallback, useLayoutEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router";
import {animate, motion, useMotionValue, useReducedMotion, type AnimationPlaybackControls} from "motion/react";
import {QrCode, Heart, MapPinned, MessageCircle, User} from "lucide-react";

import {PATHS} from "../../routing";

/** Разделы, которые может подсвечивать индикатор. QR — действие, а не раздел
 *  (docs/DESIGN_SYSTEM.md §11 П13/П2), поэтому в этот список не входит. */
type NavSection = "home" | "whereToGo" | "chats" | "profile";

/** Геометрия подсветки: где она стоит внутри капсулы и какого она размера. */
type IndicatorBox = {x: number; y: number; width: number; height: number};

/**
 * Переезд подсветки между разделами.
 *
 * Пружина без отскока: тапу не предшествовала инерция, а отскок разрешён только
 * после броска (apple-design §4, docs/DESIGN_SYSTEM.md §6.4). Длительность 0.32 с —
 * та же, что у ConfirmDialog, который docs/DESIGN_SYSTEM.md §6.4 называет эталоном
 * пружины для всего приложения.
 */
const INDICATOR_SPRING = {type: "spring", bounce: 0, duration: 0.32} as const;

/**
 * Где подсветка стояла в прошлый раз — на уровне модуля, а НЕ в состоянии
 * компонента. Это не оптимизация, без этого пункт 17 не работает вовсе.
 *
 * Каждый раздел рендерит свой экземпляр навигации: «Лента» и «Сообщения» — через
 * AppLayout, «Куда сходить» и «Профиль» — сами (см. pages/app/*.tsx), а роутер на
 * переходе размонтирует старую страницу целиком. Значит новой навигации не
 * достаётся ни состояния, ни DOM-узла предыдущей: подсветка монтируется уже на
 * новой кнопке, и никакая пружина этого не спасёт — ехать не откуда.
 *
 * Правильное лекарство — поднять навигацию в общий layout-route, но это правка
 * маршрутизации, а её на этой волне трогать нельзя. Модульная память переживает
 * размонтирование и отдаёт новой подсветке точку старта: элемент физически
 * новый, а путь на экране непрерывный (apple-design §7).
 */
let lastPlacement: {section: NavSection; box: IndicatorBox; at: number} | null = null;

/**
 * Сколько живёт эта память. Переход между разделами — это один-два кадра между
 * размонтированием и монтированием. Если прошло больше, это уже не переключение,
 * а возвращение в приложение (догружался чанк страницы, вкладка была в фоне,
 * человек вернулся кнопкой «назад» через минуту) — и проезд через всю капсулу
 * «из ниоткуда» там читался бы как глюк, а не как ответ на действие.
 */
const REMOUNT_HANDOFF_MS = 600;

type NavigationProps = {
    unreadChats: number;
    onOpenQR: () => void;
    onOpenEvents: () => void;
    onOpenHome: () => void;
    onOpenChats: () => void;
    onOpenProfile: () => void;
    /** «Куда сходить вместе»; по умолчанию — переход роутером на PATHS.whereToGo. */
    onOpenWhereToGo?: () => void;
    /** Капсула убрана, пока открыт модальный слой (#771). Полоса при этом остаётся
     *  в потоке и держит свою высоту — см. комментарий у <nav> ниже. */
    hidden?: boolean;
};

/**
 * Нижняя навигация по утверждённому прототипу /tmp/ru-love-chats.html:
 * тёмная плавающая капсула вместо светлой полосы во всю ширину.
 *
 * Продакт-оунер подтвердила скриншотом референсной панели (18.08): центральный
 * акцентный слот — QR, как в прототипе, а не магазин супер симпатий. Магазин
 * супер симпатий переехал в шапку (см. Header.tsx) — сама механика открытия
 * (dialogs.open({type: "qr" | "superLikeShop"})) не изменилась, поменялось
 * только то, какая кнопка где рендерится. Порядок кнопок — Главная/Куда
 * сходить/QR/Сообщения/Профиль: QR буквально 3-й из 5, геометрический центр,
 * а не просто «акцентный» (при первой правке поменяли только иконку на месте
 * бывшей звезды, а та стояла первой — исправлено). Распределение акцентов:
 *   • градиент — на вынесенном ДЕЙСТВИИ (QR, как в прототипе);
 *   • текущий раздел — светлый круг, без градиента;
 *   • счётчики — маленькие пилюли в углу кнопки.
 *
 * Полоса остаётся flex-элементом колонки приложения и резервирует свою высоту
 * (см. .rla-nav в styles/app.css): капсула «висит» в этом отступе и ничего не
 * перекрывает, а нижний отступ учитывает env(safe-area-inset-bottom).
 */
export const Navigation = memo(function Navigation(
    {
        unreadChats,
        onOpenQR,
        onOpenEvents,
        onOpenHome,
        onOpenChats,
        onOpenProfile,
        onOpenWhereToGo,
        hidden = false,
    }: NavigationProps) {
    const {pathname} = useLocation();
    const navRef = useRef<HTMLElement | null>(null);
    // inert — не только мышь/тап (pointer-events), но и фокус с клавиатуры и
    // скринридер: спрятанные кнопки не должны быть достижимы под модалкой.
    useLayoutEffect(() => {
        navRef.current?.toggleAttribute("inert", hidden);
    }, [hidden]);
    const navigate = useNavigate();
    const isHomeActive = pathname === "/app";
    const isChatsActive = pathname.startsWith("/app/chats");
    const isWhereToGoActive = pathname.startsWith("/where-to-go");
    const isProfileActive = pathname.startsWith("/app/profile");

    const activeSection: NavSection | null = isHomeActive
        ? "home"
        : isWhereToGoActive
            ? "whereToGo"
            : isChatsActive
                ? "chats"
                : isProfileActive
                    ? "profile"
                    : null;

    /*
     * Замечание Ирины (27.08, п. 17): «кружок внизу перескакивает». Так и было —
     * подсветка была фоном САМОЙ кнопки (.rla-nav-btn[data-on]), то есть на старом
     * разделе гасла, а на новом зажигалась. Двух состояний одного объекта не было,
     * значит и ехать было нечему.
     *
     * Теперь подсветка — один отдельный элемент на капсулу, который переезжает к
     * активной кнопке (apple-design §7: у объекта должен быть непрерывный путь).
     * Позицию берём измерением реальной кнопки, а не арифметикой по индексу:
     * капсула центрируется по ширине экрана, и любой расчёт «i-я кнопка × шаг»
     * разъехался бы ровно там, где это труднее всего заметить.
     */
    const pillRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Partial<Record<NavSection, HTMLButtonElement | null>>>({});
    const [indicator, setIndicator] = useState<IndicatorBox | null>(null);
    const reduceMotion = useReducedMotion();

    // Позиция подсветки живёт в motion-значениях, а не в пропсе animate: только так
    // её можно перехватить на полпути и увести обратно (apple-design §3 —
    // прерываемость). Пружина при этом стартует с ЖИВОЙ координаты, а не с целевой,
    // поэтому быстрый «лента → профиль → лента» не даёт скачка.
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    /** На какой кнопке подсветка стоит прямо сейчас в этом монтировании. */
    const placedSectionRef = useRef<NavSection | null>(null);
    /** Куда её везёт текущая пружина. Отличить «кнопка переехала» от «эффект
     *  прогнали повторно» можно только сравнив цель, а не позицию. */
    const targetRef = useRef<IndicatorBox | null>(null);
    const runningRef = useRef<AnimationPlaybackControls[]>([]);

    /** Везёт подсветку к коробке пружиной, всегда с её живой координаты. */
    const travelTo = useCallback((box: IndicatorBox) => {
        runningRef.current.forEach((animation) => animation.stop());
        runningRef.current = [
            animate(x, box.x, INDICATOR_SPRING),
            animate(y, box.y, INDICATOR_SPRING),
        ];
    }, [x, y]);

    const measure = useCallback(() => {
        const button = activeSection ? sectionRefs.current[activeSection] : null;
        if (!activeSection || !button || !pillRef.current) {
            setIndicator(null);
            placedSectionRef.current = null;
            return;
        }
        // offsetLeft/offsetTop считаются от .rla-nav-pill: у неё position: relative
        // именно ради этого (см. styles/app.css). getBoundingClientRect здесь не
        // годится — капсула лежит внутри .rla-nav с transform: translateZ(0), и
        // экранные координаты пришлось бы вычитать вручную.
        const next: IndicatorBox = {
            x: button.offsetLeft,
            y: button.offsetTop,
            width: button.offsetWidth,
            height: button.offsetHeight,
        };
        setIndicator((prev) =>
            prev && prev.x === next.x && prev.y === next.y
            && prev.width === next.width && prev.height === next.height
                ? prev
                : next);

        const placed = placedSectionRef.current;
        const handoff = lastPlacement;
        const previousTarget = targetRef.current;
        placedSectionRef.current = activeSection;
        targetRef.current = next;

        // Цель та же, что и у уже запущенной пружины.
        const sameTarget = previousTarget !== null
            && previousTarget.x === next.x && previousTarget.y === next.y;

        if (placed === activeSection) {
            // Подсветка уже стоит там, где надо, — делать нечего.
            if (x.get() === next.x && y.get() === next.y) return;

            // Раздел не менялся, цель не менялась, а подсветка не на месте —
            // значит пружина ещё в пути, и measure() прогнали повторно: этот же
            // layout-эффект React в dev-режиме (StrictMode) вызывает дважды, и
            // ResizeObserver отдаёт первый колбэк сразу после observe(). Раньше
            // обе ситуации попадали в ветку «встань на место» и убивали переезд
            // на первом же кадре — визуально это выглядело ровно как прежний
            // перескок. Пружину перезапускаем с живой координаты: путь остаётся
            // непрерывным (apple-design §7).
            if (sameTarget && !reduceMotion) {
                travelTo(next);
                return;
            }

            // Цель действительно переехала — поворот экрана или смена ширины
            // капсулы. Подсветка обязана оказаться на кнопке в том же кадре, что
            // и сама кнопка, а не «догонять» её пружиной.
            x.jump(next.x);
            y.jump(next.y);
            return;
        }

        // Первый замер в этом монтировании: подсветки на экране ещё нет, её надо
        // куда-то поставить. Либо на кнопку предыдущего раздела — тогда она
        // поедет (см. lastPlacement), либо сразу на свою — тогда просто появится.
        if (placed === null) {
            const from = handoff
                && handoff.section !== activeSection
                && performance.now() - handoff.at < REMOUNT_HANDOFF_MS
                ? handoff.box
                : next;
            x.jump(from.x);
            y.jump(from.y);
            if (from === next) return;
        }

        // «Уменьшить движение» — подсветка не едет, а встаёт на место (§14).
        if (reduceMotion) {
            x.jump(next.x);
            y.jump(next.y);
            return;
        }

        travelTo(next);
    }, [activeSection, reduceMotion, travelTo, x, y]);

    // Незавершённая пружина не должна тикать после размонтирования: страницу уже
    // сняли, а её подсветку — тем более.
    useLayoutEffect(() => () => {
        runningRef.current.forEach((animation) => animation.stop());
        runningRef.current = [];

        // Момент передачи следующей навигации — размонтирование, а не последний
        // замер. Между входом на страницу и уходом с неё проходят минуты, и если
        // отсчитывать REMOUNT_HANDOFF_MS от замера, окно всегда оказывается
        // просроченным — подсветка на новой странице просто появлялась на месте.
        // Окно должно мерить паузу МЕЖДУ страницами.
        const section = placedSectionRef.current;
        const box = targetRef.current;
        if (section && box) lastPlacement = {section, box, at: performance.now()};
    }, []);

    // useLayoutEffect, а не useEffect: замер и первая отрисовка подсветки должны
    // попасть в один кадр, иначе на входе в приложение она мигнёт из левого края.
    useLayoutEffect(() => {
        measure();
    }, [measure, unreadChats]);

    // Поворот экрана и смена ширины капсулы сдвигают кнопки — подсветка обязана
    // переехать вместе с ними.
    useLayoutEffect(() => {
        const pill = pillRef.current;
        if (!pill || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => measure());
        observer.observe(pill);
        return () => observer.disconnect();
    }, [measure]);

    return (
        /* hidden (#771, правка 02.10): раньше при открытии любой модалки каркас
           снимал <Navigation/> целиком. Полоса — элемент потока колонки, поэтому
           <main> в тот же кадр вырастал на её высоту (681→777 px на 390×844), а на
           закрытии новый экземпляр капсулы выскакивал без анимации на ~60-й мс,
           пока панель ещё доигрывала выход 320 мс. Это и было «вздрагивание» при
           открытии уведомлений и всплывающих окон. Теперь полоса остаётся на месте,
           а капсула уходит прозрачностью + сдвигом вниз (transform/opacity, тот же
           ритм, что у подложки модалки) — см. .rla-nav[data-hidden] в app.css. */
        <nav
            ref={navRef}
            className="rla-nav flex-shrink-0"
            {...(hidden ? {"data-hidden": "", "aria-hidden": true} : {})}
        >
            <div className="rla-nav-pill" ref={pillRef}>
                {indicator && (
                    /* Первым ребёнком капсулы и с pointer-events: none — кнопки лежат
                       после неё в потоке и остаются кликабельными поверх (⚠️ в CSS
                       про pointer-events у .rla-nav). Позицией управляет measure()
                       через x/y напрямую, поэтому ни initial, ни animate здесь нет:
                       React-пропсы перебивали бы живую пружину на каждом рендере. */
                    <motion.span
                        className="rla-nav-indicator"
                        aria-hidden="true"
                        style={{x, y, width: indicator.width, height: indicator.height}}
                    />
                )}
                <button
                    type="button"
                    aria-label="Главная"
                    title="Главная"
                    onClick={onOpenHome}
                    ref={(node) => {sectionRefs.current.home = node;}}
                    className="rla-nav-btn"
                    {...(isHomeActive ? {"data-on": ""} : {})}
                >
                    <Heart className="size-[22px]"/>
                </button>

                <button
                    type="button"
                    aria-label="Куда сходить вместе"
                    /* Через роутер, а не window.location: адрес тот же, но полная
                       перезагрузка означала бы перезапуск приложения по тапу — в том
                       числе по тапу на уже открытый раздел (панель живёт и на самом
                       экране мест). */
                    onClick={onOpenWhereToGo ?? (() => navigate(PATHS.whereToGo))}
                    ref={(node) => {sectionRefs.current.whereToGo = node;}}
                    className="rla-nav-btn"
                    {...(isWhereToGoActive ? {"data-on": ""} : {})}
                >
                    <MapPinned className="size-[22px]"/>
                </button>

                <button
                    type="button"
                    aria-label="Открыть QR-код профиля"
                    onClick={onOpenQR}
                    className="rla-nav-btn"
                    data-accent=""
                >
                    <QrCode className="size-[22px]"/>
                </button>

                <button
                    type="button"
                    aria-label="Сообщения"
                    title="Сообщения"
                    onClick={onOpenChats}
                    ref={(node) => {sectionRefs.current.chats = node;}}
                    className="rla-nav-btn"
                    {...(isChatsActive ? {"data-on": ""} : {})}
                >
                    <MessageCircle className="size-[22px]"/>
                    {/* Референс продакт-оунера (18.08, artifacts/visual/screenshots-for-review/
                        2026-08-19-nav-restyle/01-bottom-nav-reference.png) — точка без числа,
                        один в один как в макете. unreadChats (количество чатов с непрочитанными
                        сообщениями, realtime.unreadChats) по-прежнему считается и используется
                        как флаг «есть непрочитанное», просто больше не выводится цифрой —
                        точное число остаётся доступным для скринридера. */}
                    {unreadChats > 0 && (
                        <span className="rla-nav-badge" aria-hidden="true"/>
                    )}
                    {unreadChats > 0 && (
                        <span className="sr-only">{`Непрочитанных чатов: ${unreadChats}`}</span>
                    )}
                </button>


                <button
                    type="button"
                    aria-label="Профиль"
                    onClick={onOpenProfile}
                    ref={(node) => {sectionRefs.current.profile = node;}}
                    className="rla-nav-btn"
                    {...(isProfileActive ? {"data-on": ""} : {})}
                >
                    <User className="size-[22px]"/>
                </button>
            </div>
        </nav>
    );
});
